import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  customersApi,
  type CreateCustomerRequest
} from '@/api/modules/customers';
import { ApiException } from '@/api/types';
import { paths } from '@/router/paths';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];

/**
 * Create a customer. Code is auto-generated on the server. The Wh rate is
 * a decimal in per-Wh units (₹12, ₹13, etc.) — the quotation form multiplies
 * it by voltage × capacity for EV Battery pricing.
 */
export function CustomerCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateCustomerRequest>({
    name: '',
    taxId: '',
    email: '',
    phone: '',
    perWhRate: '12',
    currency: 'INR'
  });

  const mutation = useMutation({
    mutationFn: (req: CreateCustomerRequest) =>
      customersApi.create({
        ...req,
        taxId: req.taxId?.trim() || undefined,
        email: req.email?.trim() || undefined,
        phone: req.phone?.trim() || undefined
      }),
    onSuccess: (created) => {
      navigate(paths.customerDetail.replace(':id', created.id), { replace: true });
    }
  });

  const fieldErrors = extractFieldErrors(mutation.error);
  const topLevelError =
    mutation.error && !(mutation.error instanceof ApiException)
      ? (mutation.error as Error).message
      : mutation.error instanceof ApiException && !mutation.error.error.fieldErrors?.length
        ? mutation.error.error.message
        : null;

  const handleChange =
    (field: keyof CreateCustomerRequest) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); mutation.mutate(form); }} noValidate>
      <Typography variant="h1" gutterBottom>
        New customer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Code is generated automatically (<code>CUST-YYYY-000001</code>).
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Name"
                helperText={fieldErrors.name ?? 'Displayed on quotations and orders.'}
                error={Boolean(fieldErrors.name)}
                value={form.name}
                onChange={handleChange('name')}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tax ID / GSTIN"
                helperText={fieldErrors.taxId ?? 'Optional'}
                error={Boolean(fieldErrors.taxId)}
                value={form.taxId ?? ''}
                onChange={handleChange('taxId')}
                inputProps={{ maxLength: 64 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                helperText={fieldErrors.phone ?? 'Optional'}
                error={Boolean(fieldErrors.phone)}
                value={form.phone ?? ''}
                onChange={handleChange('phone')}
                inputProps={{ maxLength: 64 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                helperText={fieldErrors.email ?? 'Optional'}
                error={Boolean(fieldErrors.email)}
                value={form.email ?? ''}
                onChange={handleChange('email')}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                label="Rate per Wh"
                helperText={fieldErrors.perWhRate ?? 'Used to price EV battery quotations. Example: 12 → ₹12 per Wh.'}
                error={Boolean(fieldErrors.perWhRate)}
                value={form.perWhRate}
                onChange={handleChange('perWhRate')}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{form.currency}</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/ Wh</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                select
                label="Currency"
                error={Boolean(fieldErrors.currency)}
                helperText={fieldErrors.currency ?? undefined}
                value={form.currency}
                onChange={handleChange('currency')}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {topLevelError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {topLevelError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          type="button"
          variant="text"
          onClick={() => navigate(paths.customers)}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Creating…' : 'Create customer'}
        </Button>
      </Stack>
    </Box>
  );
}

function extractFieldErrors(err: unknown): Record<string, string | undefined> {
  if (!(err instanceof ApiException)) return {};
  const fields = err.error.fieldErrors ?? [];
  const out: Record<string, string> = {};
  for (const fe of fields) out[fe.field] = fe.message;
  return out;
}
